(function () {
  var STORAGE_KEY = 'ratniva:recently-viewed:v1';
  var MAX_STORED_PRODUCTS = 12;
  var HISTORY_TTL = 30 * 24 * 60 * 60 * 1000;

  function normaliseHandle(handle) {
    return typeof handle === 'string' ? handle.trim().toLowerCase() : '';
  }

  function readHistory() {
    try {
      var parsed = JSON.parse(localStorage.getItem(STORAGE_KEY));
      var now = Date.now();

      if (!Array.isArray(parsed)) return [];

      return parsed
        .map(function (item) {
          if (typeof item === 'string') {
            return { handle: normaliseHandle(item), viewedAt: now };
          }

          return {
            handle: normaliseHandle(item && item.handle),
            viewedAt: Number(item && item.viewedAt) || 0,
          };
        })
        .filter(function (item, index, items) {
          return (
            item.handle &&
            item.viewedAt > now - HISTORY_TTL &&
            items.findIndex(function (candidate) {
              return candidate.handle === item.handle;
            }) === index
          );
        })
        .slice(0, MAX_STORED_PRODUCTS);
    } catch (error) {
      return [];
    }
  }

  function writeHistory(history) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
      return true;
    } catch (error) {
      return false;
    }
  }

  function recordProduct(handle) {
    var productHandle = normaliseHandle(handle);
    if (!productHandle) return;

    var history = readHistory().filter(function (item) {
      return item.handle !== productHandle;
    });

    history.unshift({ handle: productHandle, viewedAt: Date.now() });

    if (writeHistory(history.slice(0, MAX_STORED_PRODUCTS))) {
      document.dispatchEvent(new CustomEvent('recently-viewed:updated'));
    }
  }

  if (!customElements.get('recently-viewed-tracker')) {
    customElements.define(
      'recently-viewed-tracker',
      class RecentlyViewedTracker extends HTMLElement {
        connectedCallback() {
          recordProduct(this.dataset.productHandle);
        }
      }
    );
  }

  if (!customElements.get('recently-viewed-products')) {
    customElements.define(
      'recently-viewed-products',
      class RecentlyViewedProducts extends HTMLElement {
        constructor() {
          super();
          this.abortController = null;
          this.handleHistoryUpdate = this.render.bind(this);
          this.handleStorageUpdate = this.onStorageUpdate.bind(this);
          this.handleClick = this.onClick.bind(this);
          this.handleScroll = this.updateControls.bind(this);
          this.resizeObserver = new ResizeObserver(this.updateControls.bind(this));
        }

        connectedCallback() {
          document.addEventListener('recently-viewed:updated', this.handleHistoryUpdate);
          window.addEventListener('storage', this.handleStorageUpdate);
          this.addEventListener('click', this.handleClick);

          var grid = this.querySelector('[data-recently-viewed-grid]');
          if (grid) {
            grid.addEventListener('scroll', this.handleScroll, { passive: true });
            this.resizeObserver.observe(grid);
          }

          this.render();
        }

        disconnectedCallback() {
          document.removeEventListener('recently-viewed:updated', this.handleHistoryUpdate);
          window.removeEventListener('storage', this.handleStorageUpdate);
          this.removeEventListener('click', this.handleClick);

          var grid = this.querySelector('[data-recently-viewed-grid]');
          if (grid) grid.removeEventListener('scroll', this.handleScroll);

          this.resizeObserver.disconnect();
          this.abortController?.abort();
        }

        onStorageUpdate(event) {
          if (event.key === STORAGE_KEY) this.render();
        }

        onClick(event) {
          var previousButton = event.target.closest('[data-recently-viewed-previous]');
          var nextButton = event.target.closest('[data-recently-viewed-next]');
          if (!previousButton && !nextButton) return;

          var grid = this.querySelector('[data-recently-viewed-grid]');
          var firstItem = grid?.querySelector('.product-recently-viewed__item');
          if (!grid || !firstItem) return;

          var gap = parseFloat(getComputedStyle(grid).columnGap) || 0;
          var distance = firstItem.getBoundingClientRect().width + gap;
          grid.scrollBy({ left: previousButton ? -distance : distance, behavior: 'smooth' });
        }

        updateControls() {
          var grid = this.querySelector('[data-recently-viewed-grid]');
          var controls = this.querySelector('[data-recently-viewed-controls]');
          if (!grid || !controls) return;

          var hasOverflow = grid.scrollWidth > grid.clientWidth + 2;
          controls.hidden = !hasOverflow;
          if (!hasOverflow) return;

          var previousButton = controls.querySelector('[data-recently-viewed-previous]');
          var nextButton = controls.querySelector('[data-recently-viewed-next]');
          var maxScrollLeft = grid.scrollWidth - grid.clientWidth;

          if (previousButton) previousButton.disabled = grid.scrollLeft <= 2;
          if (nextButton) nextButton.disabled = grid.scrollLeft >= maxScrollLeft - 2;
        }

        getProductHandles() {
          var currentProductHandle = normaliseHandle(this.dataset.currentProductHandle);
          var limit = Math.max(1, Number(this.dataset.productsToShow) || 4);

          return readHistory()
            .map(function (item) {
              return item.handle;
            })
            .filter(function (handle) {
              return handle !== currentProductHandle;
            })
            .slice(0, limit);
        }

        buildCardUrl(handle) {
          return this.dataset.cardUrlTemplate.replace('__handle__', encodeURIComponent(handle));
        }

        async fetchCard(handle, signal) {
          var response = await fetch(this.buildCardUrl(handle), {
            headers: { Accept: 'text/html' },
            signal: signal,
          });

          if (!response.ok) return '';
          return (await response.text()).trim();
        }

        async render() {
          var grid = this.querySelector('[data-recently-viewed-grid]');
          if (!grid || !this.dataset.cardUrlTemplate) return;

          this.abortController?.abort();
          this.abortController = new AbortController();
          var signal = this.abortController.signal;
          var handles = this.getProductHandles();

          this.hidden = true;
          this.setAttribute('aria-busy', 'true');
          grid.replaceChildren();

          if (!handles.length) {
            this.removeAttribute('aria-busy');
            return;
          }

          try {
            var cardRequests = await Promise.allSettled(
              handles.map(
                function (handle) {
                  return this.fetchCard(handle, signal);
                }.bind(this)
              )
            );

            if (signal.aborted || !this.isConnected) return;

            cardRequests.forEach(function (request) {
              var cardHtml = request.status === 'fulfilled' ? request.value : '';
              if (!cardHtml) return;

              var item = document.createElement('li');
              item.className = 'product-recently-viewed__item';
              item.innerHTML = cardHtml;
              grid.appendChild(item);
            });

            if (grid.children.length) {
              this.hidden = false;
              requestAnimationFrame(this.updateControls.bind(this));
              document.dispatchEvent(new CustomEvent('wishlist:updated'));
            }
          } catch (error) {
            if (error.name !== 'AbortError') {
              console.error('Recently viewed products could not be loaded.', error);
            }
          } finally {
            if (!signal.aborted) this.removeAttribute('aria-busy');
          }
        }
      }
    );
  }
})();
